import { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { getFrameDisplayBounds } from 'redux/db/util';
import { DEFAULT_CANVAS_DROP_SHADOW_MODE, selectActiveScene } from 'redux/db/reducers';
import { Frame, FrameStatus } from 'dragonmouse-shared/interfaces';
import { Rect } from 'dragonmouse-shared/geometry';
import * as S from './styles';
import * as Base from '../styles';
import Icon, { IconType } from 'components/Icon';
import Moon from 'components/Widget/Moon';
import { useImageLoading } from 'hooks/useImageLoading';
import { useFrameMoonProgress } from 'hooks/useFrameMoonProgress';
import { unthemed } from 'tokens';
import { frameImageUrl } from 'utils';

interface Props {
	frame: Frame;
}

const STATUS_MAX_CHARS = 60;

// Scene-coord sizes — scale naturally with the canvas transform.
const MOON_SIZE = 100;
const STATUS_SIZE = 32;
const STATUS_GAP = 40;

// Most frames are above these thresholds and get the constant sizes above.
// Below each threshold, the corresponding dimension scales linearly toward 0
// so a too-small frame doesn't get a cropped moon or overflowing caption.
const WIDTH_SCALE_THRESHOLD = 400;
const HEIGHT_SCALE_THRESHOLD = 350;

const truncate = (s: string, max: number) => (s.length > max ? s.slice(0, max).trimEnd() + '…' : s);

//------------------------------------------------------------------------------------------------
// FRAMEVIEW
//------------------------------------------------------------------------------------------------

const FrameView = ({ frame }: Props) => {
	const [bounds, setBounds] = useState<Rect>({ x: 0, y: 0, width: 0, height: 0 });
	const imageSrc = frameImageUrl(frame);
	const [isImageLoading, handleImageRef] = useImageLoading(imageSrc ?? '');

	const scene = useSelector(
		(r: RootReducer) => selectActiveScene(r.db, r.mode.activeSceneId),
		shallowEqual
	);

	const isProcessing = frame.metadata.status === FrameStatus.PROCESSING;
	const isError = frame.metadata.status === FrameStatus.ERROR;
	const isOverlayVisible = frame.metadata.status !== FrameStatus.READY || isImageLoading;

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	useEffect(() => {
		setBounds(getFrameDisplayBounds(frame));
	}, [frame.x, frame.y, frame.width, frame.height, frame.metadata?.displayScale]);

	//------------------------------------------------------------------------------------------------
	// UTILITY
	//------------------------------------------------------------------------------------------------

	const { progress: moonProgress, isIndeterminate } = useFrameMoonProgress(frame, isImageLoading);

	const caption =
		isProcessing || isError
			? truncate(frame.metadata.statusMessage ?? '', STATUS_MAX_CHARS)
			: isImageLoading
				? 'Loading frame…'
				: '';

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	// Width drives font size; height drives the moon-to-caption gap; moon takes
	// the smaller of the two so it fits both dimensions. Above the thresholds
	// every scale is clamped to 1 — i.e. the constant sizes above are unchanged.
	const widthScale = Math.min(1, frame.width / WIDTH_SCALE_THRESHOLD);
	const heightScale = Math.min(1, frame.height / HEIGHT_SCALE_THRESHOLD);
	const moonScale = Math.min(widthScale, heightScale);
	const moonSize = MOON_SIZE * moonScale;
	const statusFontSize = STATUS_SIZE * widthScale;
	const statusGap = STATUS_GAP * heightScale;

	const renderErrorContent = () => (
		<S.ErrorBlock>
			<Icon type={IconType.ERROR} width={moonSize} height={moonSize} color={unthemed.gray[100]} />
			{caption && (
				<S.Caption fontSize={statusFontSize} marginTop={statusGap}>
					{caption}
				</S.Caption>
			)}
		</S.ErrorBlock>
	);

	const renderCenterStack = () => (
		<S.CenterStack>
			<Moon progress={moonProgress} size={moonSize} isBreathing={isIndeterminate} />
			{caption && (
				<S.ShimmerCaption fontSize={statusFontSize} marginTop={statusGap}>
					{caption}
				</S.ShimmerCaption>
			)}
		</S.CenterStack>
	);

	const renderOverlay = () => (
		<S.Overlay>{isError ? renderErrorContent() : renderCenterStack()}</S.Overlay>
	);

	const renderContents = () => {
		if (!imageSrc) return null;
		return (
			<img
				key={imageSrc}
				ref={handleImageRef}
				src={imageSrc}
				width={bounds.width}
				height={bounds.height}
			/>
		);
	};

	return (
		<>
			<S.Base sceneBounds={bounds} backgroundColor={frame.metadata.backgroundColor}>
				{renderContents()}
				{isOverlayVisible && renderOverlay()}
			</S.Base>
			<Base.DropShadow
				sceneBounds={bounds}
				dropShadowMode={scene?.metadata?.canvasDropShadowMode || DEFAULT_CANVAS_DROP_SHADOW_MODE}
			/>
		</>
	);
};

export default FrameView;
